'use client'

import { useEffect, useState, type ReactNode } from 'react'

/**
 * IphoneFrame: wrapper que mostra children fullscreen em mobile e
 * dentro de um mockup de iPhone em desktop (sm+).
 *
 * Inclui barra de status com horario ao vivo + signal/wifi/battery
 * e Dynamic Island centralizado.
 */

function getHHMM(): string {
  const d = new Date()
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

function StatusBar({ time }: { time: string }) {
  return (
    <div className="hidden sm:flex absolute top-3 left-0 right-0 px-7 z-40 items-center justify-between text-white text-[14px] font-semibold pointer-events-none select-none">
      {/* Esquerda: hora */}
      <span className="tabular-nums">{time}</span>

      {/* Direita: signal + wifi + battery */}
      <div className="flex items-center gap-1.5">
        {/* Signal cells (4 barras) */}
        <svg width="16" height="11" viewBox="0 0 16 11" fill="none" aria-hidden>
          <rect x="0" y="7" width="3" height="4" rx="0.5" fill="currentColor" />
          <rect x="4" y="5" width="3" height="6" rx="0.5" fill="currentColor" />
          <rect x="8" y="3" width="3" height="8" rx="0.5" fill="currentColor" />
          <rect x="12" y="0" width="3" height="11" rx="0.5" fill="currentColor" />
        </svg>

        {/* Wifi */}
        <svg width="16" height="11" viewBox="0 0 16 11" fill="none" aria-hidden>
          <path
            d="M8 1C5.2 1 2.7 1.95 0.8 3.55C0.55 3.77 0.55 4.15 0.78 4.38L1.97 5.57C2.18 5.78 2.51 5.78 2.72 5.58C4.13 4.26 6.0 3.5 8 3.5C10 3.5 11.87 4.26 13.28 5.58C13.49 5.78 13.82 5.78 14.03 5.57L15.22 4.38C15.45 4.15 15.45 3.77 15.2 3.55C13.3 1.95 10.8 1 8 1Z"
            fill="currentColor"
          />
          <path
            d="M8 5C6.42 5 4.95 5.55 3.85 6.5C3.6 6.72 3.59 7.1 3.83 7.34L4.99 8.5C5.2 8.71 5.53 8.72 5.74 8.51C6.34 7.96 7.13 7.62 8 7.62C8.87 7.62 9.66 7.96 10.26 8.51C10.47 8.72 10.8 8.71 11.01 8.5L12.17 7.34C12.41 7.1 12.4 6.72 12.15 6.5C11.05 5.55 9.58 5 8 5Z"
            fill="currentColor"
          />
          <circle cx="8" cy="10" r="1.2" fill="currentColor" />
        </svg>

        {/* Battery (~80% level) */}
        <div className="flex items-center gap-[1px]">
          <div className="relative w-[24px] h-[11px] rounded-[3px] border border-white/80 px-[1px] py-[1px]">
            <div className="h-full w-[80%] rounded-[1.5px] bg-white" />
          </div>
          <div className="w-[1.5px] h-[4px] bg-white/80 rounded-r-sm" />
        </div>
      </div>
    </div>
  )
}

export function IphoneFrame({ children }: { children: ReactNode }) {
  const [time, setTime] = useState('00:00')

  useEffect(() => {
    setTime(getHHMM())
    const id = setInterval(() => setTime(getHHMM()), 20000)
    return () => clearInterval(id)
  }, [])

  return (
    <div className="min-h-dvh sm:flex sm:items-center sm:justify-center sm:bg-gradient-to-br sm:from-stone-900 sm:via-emerald-950 sm:to-stone-900 sm:p-6 sm:py-10">
      <div
        className="
          w-full h-dvh
          sm:relative sm:w-[390px] sm:h-[820px] sm:max-h-[90vh]
          sm:bg-stone-950 sm:rounded-[3.5rem] sm:p-3
          sm:shadow-[0_20px_70px_-20px_rgba(0,0,0,0.6),0_0_0_3px_rgba(255,255,255,0.04)_inset]
          sm:ring-1 sm:ring-stone-800
        "
      >
        {/* Status bar com hora + signal/wifi/battery (so desktop) */}
        <StatusBar time={time} />

        {/* Dynamic Island */}
        <div className="hidden sm:flex absolute top-3 left-1/2 -translate-x-1/2 z-50 w-[120px] h-[34px] bg-black rounded-full items-center justify-end pr-2 gap-1.5">
          <div className="w-2 h-2 bg-stone-800 rounded-full" aria-hidden />
          <div className="w-1 h-1 bg-stone-700 rounded-full" aria-hidden />
        </div>

        {/* Screen: rounded inner */}
        <div className="w-full h-full sm:rounded-[2.8rem] sm:overflow-hidden">
          {children}
        </div>
      </div>
    </div>
  )
}
